import "./App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import SeoManager from "./components/SeoManager";
import CookieBanner from "./components/CookieBanner";
import AuthCallback from "./auth/AuthCallback";
import ProtectedRoute from "./auth/ProtectedRoute";
// HomePage is the primary landing page and the LCP-critical route, so it is
// imported eagerly (bundled into main.js) to avoid a second chunk round-trip
// before the hero renders on real mobile devices. Other routes stay lazy.
import HomePage from "./components/HomePage";

// Route-level code-splitting: each page ships as its own chunk so a visitor only
// downloads the JS for the page they land on (big mobile TBT/LCP win).
const ContactPage = lazy(() => import("./components/ContactPage"));
const DashboardsPage = lazy(() => import("./components/DashboardsPage"));
const AssessmentPage = lazy(() => import("./components/AssessmentPage"));
const DelawarePage = lazy(() => import("./components/DelawarePage"));
const PhiladelphiaPage = lazy(() => import("./components/PhiladelphiaPage"));
const CareersPage = lazy(() => import("./components/CareersPage"));
const ServicesHubPage = lazy(() => import("./components/ServicesHubPage"));
const GovernmentPage = lazy(() => import("./components/GovernmentPage"));
const SubmitTestimonialPage = lazy(() => import("./components/SubmitTestimonialPage"));
const AdminTestimonialsPage = lazy(() => import("./components/AdminTestimonialsPage"));
const TestimonialGalleryPage = lazy(() => import("./components/TestimonialGalleryPage"));
const AboutAuthorPage = lazy(() => import("./components/AboutAuthorPage"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));

const CaseStudiesIndexPage = lazy(() => import("./components/CaseStudyPages").then((m) => ({ default: m.CaseStudiesIndexPage })));
const CaseStudyDetailPage = lazy(() => import("./components/CaseStudyPages").then((m) => ({ default: m.CaseStudyDetailPage })));
const InsightsIndexPage = lazy(() => import("./components/InsightsPages").then((m) => ({ default: m.InsightsIndexPage })));
const ArticleDetailPage = lazy(() => import("./components/InsightsPages").then((m) => ({ default: m.ArticleDetailPage })));
const InsightsCategoryPage = lazy(() => import("./components/InsightsPages").then((m) => ({ default: m.CategoryPage })));
const TermsPage = lazy(() => import("./components/legal/LegalPages").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("./components/legal/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const EEOPage = lazy(() => import("./components/legal/LegalPages").then((m) => ({ default: m.EEOPage })));
const ADAPage = lazy(() => import("./components/legal/LegalPages").then((m) => ({ default: m.ADAPage })));
const BiDashboardsPage = lazy(() => import("./components/services/ServicePages").then((m) => ({ default: m.BiDashboardsPage })));
const DataStrategyPage = lazy(() => import("./components/services/ServicePages").then((m) => ({ default: m.DataStrategyPage })));
const AdvancedAnalyticsPage = lazy(() => import("./components/services/ServicePages").then((m) => ({ default: m.AdvancedAnalyticsPage })));
const TableauConsultingPage = lazy(() => import("./components/services/ServicePages").then((m) => ({ default: m.TableauConsultingPage })));
const PowerBiConsultingPage = lazy(() => import("./components/services/ServicePages").then((m) => ({ default: m.PowerBiConsultingPage })));

const PageLoader = () => (
  <div className="pa-page-loader" aria-busy="true" aria-label="Loading">
    <span className="pa-page-spinner" />
  </div>
);

// Redirects for legacy/removed URLs still indexed by search engines. These mirror the
// static redirect stubs in scripts/prerender.js (which handle direct hits + crawlers via
// raw HTML); these SPA routes cover in-app navigation and client-rendered hits. Keep the
// two lists in sync.
const LEGACY_REDIRECTS = [
  ["/insights/how-much-does-a-tableau-dashboard-cost", "/insights/tableau-dashboard-cost-2026"],
  ["/get-started", "/analytics-assessment"],
  ["/solutions", "/services"],
  ["/blog", "/insights"],
  ["/blog/copilot-in-power-bi", "/insights/copilot-in-power-bi"],
  ["/blog/etl-vs-elt", "/insights/etl-vs-elt"],
  ["/blog/student-performance-dashboard", "/insights/student-performance-dashboard"],
  ["/blog/star-schema-vs-snowflake-schema", "/insights/star-schema-vs-snowflake-schema"],
  ["/blog/data-quality-metrics", "/insights/data-quality-metrics"],
  ["/blog/lakehouse-vs-warehouse", "/insights/lakehouse-vs-warehouse"],
  ["/blog/healthcare-kpi-dashboard", "/insights/healthcare-kpi-dashboard-playbook"],
  ["/blog/month-end-close-dashboard", "/insights/how-to-build-a-month-end-close-dashboard"],
  ["/blog/executive-dashboard-examples", "/insights/executive-dashboard-examples"],
  ["/blog/tableau-executive-dashboard", "/insights/tableau-executive-dashboard"],
  ["/blog/power-bi-for-fpa", "/insights/power-bi-for-fpa"],
  ["/blog/power-bi-embedded-analytics", "/insights/power-bi-embedded-analytics"],
  ["/blog/tableau-embedded-analytics", "/insights/tableau-embedded-analytics"],
  ["/blog/hipaa-compliant-analytics", "/insights/hipaa-compliant-analytics"],
  ["/pricing", "/analytics-assessment"],
  ["/book-a-demo", "/analytics-assessment"],
  ["/demo", "/analytics-assessment"],
  ["/consultation", "/analytics-assessment"],
  ["/free-assessment", "/analytics-assessment"],
  ["/book-a-call", "/analytics-assessment"],
  ["/about-us", "/about/frederick-soh"],
  ["/team", "/about/frederick-soh"],
  ["/our-work", "/case-studies"],
  ["/portfolio", "/case-studies"],
  ["/work", "/case-studies"],
  ["/contact-us", "/contact"],
  ["/home", "/"],
  ["/index", "/"],
];

function AppRouter() {
  const location = useLocation();
  // Handle the OAuth return (session_id in the URL fragment) before any route/auth check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesHubPage />} />
        <Route path="/government" element={<GovernmentPage />} />
        <Route path="/services/bi-dashboards" element={<BiDashboardsPage />} />
        <Route path="/services/data-strategy" element={<DataStrategyPage />} />
        <Route path="/services/advanced-analytics" element={<AdvancedAnalyticsPage />} />
        <Route path="/services/tableau-consulting" element={<TableauConsultingPage />} />
        <Route path="/services/power-bi-consulting" element={<PowerBiConsultingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/dashboards" element={<DashboardsPage />} />
        <Route path="/analytics-assessment" element={<AssessmentPage />} />
        <Route path="/delaware" element={<DelawarePage />} />
        <Route path="/philadelphia" element={<PhiladelphiaPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/about/frederick-soh" element={<AboutAuthorPage />} />
        <Route path="/case-studies" element={<CaseStudiesIndexPage />} />
        <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
        <Route path="/insights" element={<InsightsIndexPage />} />
        <Route path="/insights/category/:slug" element={<InsightsCategoryPage />} />
        {LEGACY_REDIRECTS.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        <Route path="/blog/*" element={<Navigate to="/insights" replace />} />
        <Route path="/insights/:slug" element={<ArticleDetailPage />} />
        <Route path="/submit-testimonial" element={<SubmitTestimonialPage />} />
        <Route path="/testimonials" element={<TestimonialGalleryPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/eeo" element={<EEOPage />} />
        <Route path="/ada" element={<ADAPage />} />
        <Route path="/admin" element={<ProtectedRoute><AdminTestimonialsPage /></ProtectedRoute>} />
        <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonialsPage /></ProtectedRoute>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

// Mount the chat widget only once the browser is idle so it never competes with
// LCP-critical resources during the initial page load.
function DeferredChat() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const ric = window.requestIdleCallback;
    if (ric) {
      const id = ric(() => setShow(true), { timeout: 3000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.star_color) document.documentElement.style.setProperty("--star-color", d.star_color); })
      .catch(() => {});
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <SeoManager />
        <DeferredChat />
        <CookieBanner />
      </BrowserRouter>
    </div>
  );
}

export default App;
